import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { useEffect, useState } from "react";
import { Button, message } from "antd";
import styled from "@emotion/styled";
import { PriceDisplay } from "../../Users/MyProfile";
import { URL } from "../../helper/Mainurl";
import { useSubscriptions } from "../../contexts/subscriptionsContext";

const Container = styled.div`
  max-width: 600px;
  margin: 10px auto;
  background: #fff;
  padding: 8px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 18px;
`;

const PlanBox = styled.div`
  border: 2px solid #a855f7;
  border-radius: 12px;
  padding: 16px 20px;
  background: #faf5ff;
  margin-bottom: 16px;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #7e22ce;
  }

  p {
    font-size: 14px;
    color: #4b5563;
    margin: 4px 0 0;
  }

  .price {
    font-size: 16px;
    font-weight: 500;
    color: #1f2937;
    margin-top: 8px;
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 768px) {
    .responsive-grid {
      grid-template-columns: 1fr 1fr;
    }
  }
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  border: none;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
`;

const StripeInput = styled.div`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
`;

const ResponsiveGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 12px;

  & > div {
    flex: 1;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;

const PaymentForm = ({
  subscriptionData,
  selectedPlan: initialSelectedPlan,
  setSelectedPlan,
  onBack,
  onSubscribed,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { fetchSubscriptions } = useSubscriptions();

  const [loading, setLoading] = useState(false);
  const [cardholderName, setCardholderName] = useState("");
  const [selected, setSelected] = useState(initialSelectedPlan);

  useEffect(() => {
    setSelected(initialSelectedPlan);
  }, [initialSelectedPlan]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);

    const cardNumber = elements.getElement(CardNumberElement);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardNumber,
      billing_details: { name: cardholderName },
    });

    if (error) {
      message.success(error.message);
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("subscription_id", selected.subscription_id);
    formData.append("payment_method", paymentMethod.id);

    try {
      const response = await fetch(`${URL}/v1/subscribe`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: formData,
      });

      const result = await response.json();
      // console.log("result_payment_outside", result);
      if (!response.ok) {
        message.error(result.message || "Subscription failed.");
        return; // Stop further execution
      }

      onSubscribed();
      message.success(result.message || "Subscription successful.");
      fetchSubscriptions();
    } catch (err) {
      message.success(err.message || "Something went wrong. Please try again.");
      setLoading(false);
      return;
    } finally {
      setLoading(false);
    }

    setLoading(false);
  };

  return (
    <Container>
      <SectionTitle>Selected Plan</SectionTitle>
      {selected && (
        <PlanBox>
          <h3>{selected.title}</h3>
          <p>{selected.description || "No description provided."}</p>
          {console.log(selected)}
          {/* <div className="price">
            {selected.special_price
              ? `$${selected.special_price}`
              : `$${selected.price}`}
          </div> */}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <PriceDisplay
              price={
                selected.special_price ? selected.special_price : selected.price
              }
              billingCycle={selected.billing_cycle}
            />
          </div>
        </PlanBox>
      )}

      <StyledForm onSubmit={handleSubmit}>
        <SectionTitle>Payment Details</SectionTitle>

        <ResponsiveGrid>
          <div>
            <Label>Cardholder Name</Label>
            <StripeInput>
              <Input
                type="text"
                placeholder="John Doe"
                value={cardholderName}
                onChange={(e) => setCardholderName(e.target.value)}
                required
              />
            </StripeInput>
          </div>

          <div>
            <Label>Card Number</Label>
            <StripeInput>
              <CardNumberElement
                options={{ style: { base: { fontSize: "14px" } } }}
              />
            </StripeInput>
          </div>
        </ResponsiveGrid>

        <Row>
          <div>
            <Label>Expiry</Label>
            <StripeInput>
              <CardExpiryElement
                options={{ style: { base: { fontSize: "14px" } } }}
              />
            </StripeInput>
          </div>
          <div>
            <Label>CVV</Label>
            <StripeInput>
              <CardCvcElement
                options={{ style: { base: { fontSize: "14px" } } }}
              />
            </StripeInput>
          </div>
        </Row>

        <ButtonRow>
          <Button onClick={onBack}>Back</Button>
          <Button
            type="primary"
            htmlType="submit"
            disabled={loading || !stripe}
          >
            {loading ? "Processing..." : "Subscribe & Pay"}
          </Button>
        </ButtonRow>
      </StyledForm>
    </Container>
  );
};

export default PaymentForm;
